import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaAward, FaUsers, FaHardHat } from 'react-icons/fa';
import HeroSlider from '../components/HeroSlider';
import './Home.css';
import { heroImages, stats, coreValues, services, projects, testimonials } from '../data/siteData';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="home-page">
      {/* Hero Slider */}
      <HeroSlider slides={heroImages} />

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.id}
                className="stat-card"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="stat-icon">{stat.icon}</div>
                <h3 className="stat-number">{stat.number}</h3>
                <p className="stat-label">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="container">
          <motion.div {...fadeInUp} className="section-header">
            <h2>Welcome to NovaBuild Constructions</h2>
            <p>
              Your trusted partner in construction excellence. We bring innovation, 
              quality, and reliability to every project we undertake.
            </p>
          </motion.div>

          <div className="welcome-grid">
            <motion.div {...fadeInUp} className="welcome-content">
              <h3>Building Dreams into Reality</h3>
              <p>
                With over 15 years of experience in the construction industry, 
                NovaBuild Constructions has established itself as a leader in 
                residential and commercial construction projects.
              </p>
              <p>
                Our team of experienced engineers, architects, and skilled workers 
                are committed to delivering exceptional results that exceed expectations.
              </p>
              <ul className="feature-list">
                <li><FaCheckCircle /> Licensed & Insured</li>
                <li><FaCheckCircle /> Quality Craftsmanship</li>
                <li><FaCheckCircle /> On-Time Delivery</li>
                <li><FaCheckCircle /> Competitive Pricing</li>
              </ul>
              <Link to="/about" className="btn btn-primary">
                Learn More About Us
              </Link>
            </motion.div>

            <motion.div 
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="welcome-image"
            >
              <img 
                src={require('../assets/images/fff.jpg')} 
                alt="Construction Excellence" 
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="values-section bg-light">
        <div className="container">
          <motion.div {...fadeInUp} className="section-header">
            <h2>Our Core Values</h2>
            <p>The principles that guide everything we do</p>
          </motion.div>

          <div className="values-grid">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.id}
                className="value-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview">
        <div className="container">
          <motion.div {...fadeInUp} className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive construction solutions for all your needs</p>
          </motion.div>

          <div className="services-grid">
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={service.id}
                className="service-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, boxShadow: "0 12px 40px rgba(0,0,0,0.15)" }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to="/services" className="service-link">
                  Learn More →
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-4">
            <Link to="/services" className="btn btn-primary btn-large">
              View All Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="projects-preview bg-light">
        <div className="container">
          <motion.div {...fadeInUp} className="section-header">
            <h2>Featured Projects</h2>
            <p>Showcasing our excellence in construction</p>
          </motion.div>

          <div className="projects-grid">
            {projects.slice(0, 3).map((project, index) => (
              <motion.div
                key={project.id}
                className="project-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} loading="lazy" />
                  <div className="project-overlay">
                    <span className="project-category">{project.category}</span>
                  </div>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p className="project-location">📍 {project.location}</p>
                  <p>{project.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-4">
            <Link to="/projects" className="btn btn-primary btn-large">
              View All Projects
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div {...fadeInUp} className="section-header">
            <h2>What Our Clients Say</h2>
            <p>Real feedback from our satisfied customers</p>
          </motion.div>

          <div className="testimonials-grid">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="testimonial-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="testimonial-stars">
                  {'⭐'.repeat(testimonial.rating)}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.position} - {testimonial.company}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div 
            {...fadeInUp}
            className="cta-content"
          >
            <h2>Ready to Start Your Project?</h2>
            <p>
              Let's build something amazing together. Contact us today for a free consultation.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">
                Get Free Quote
              </Link>
              <a href="+91 9767610009" className="btn btn-outline btn-large">
                Call Us Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
